import { PageDataHandler } from "@/lib/utility/data/page-data-handler";
import { Company, ICompany } from "@/models/company.entity";
import companyService from "@/services/mrfiktiv/services/companyService";
import {
  MrfiktivCompanyControllerGetParamsGen,
  MrfiktivMultipleIdsDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import store from "@/store/VuexPlugin";
import { Action, Module, Mutation, getModule } from "vuex-module-decorators";
import { PaginatedBaseStore } from "../paginated-base.store";
import { CompanyDataAccessLayer } from "./access-layers/company.access-layer";
import { PaginationFilterListElement } from "./base-pagination.store";
import { CompanyPageDataProvider } from "./page-data-provider/company.page-data-provider";
import { PageFilterElement } from "@/models/page-filter-element.entity";
import { PageFilterOperationEnum } from "@/lib/utility/data/page-filter-operation.enum";
import { PartnerModule } from "./partner";
import { UserModule } from "./me-user.store";
import { ActionEnum } from "../enum/authActionEnum";
import { ResourceEnum } from "../enum/authResourceEnum";

@Module({
  dynamic: true,
  namespaced: true,
  name: "company",
  store
})
export class CompanyPaginationStore extends PaginatedBaseStore<ICompany, MrfiktivCompanyControllerGetParamsGen> {
  protected _data = CompanyDataAccessLayer;
  protected _pageProvider = CompanyPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  filterOptions: PaginationFilterListElement[] = Company.filterables;

  partner: ICompany | null = null;

  @Action
  async fetchPartner(partnerId: string) {
    const response = await companyService.get({
      partnerId,
      filter: [
        new PageFilterElement({
          key: "isPartner",
          operation: PageFilterOperationEnum.EQUAL,
          value: true as any
        })
      ]
    });

    const company = response.data?.[0];

    if (!company) {
      return;
    }

    const c = new Company(company);
    this._data.set(c);
    this.context.commit("addPartner", c);
    return c;
  }

  @Mutation
  addPartner(company: ICompany) {
    this.partner = company;
  }

  @Action
  async getMany(data: { partnerId: string; data: MrfiktivMultipleIdsDtoGen }) {
    const response = await companyService.getMany(data.partnerId, data.data);

    const companies = response.map((c) => new Company(c));

    this.context.commit("addMany", companies);

    return companies;
  }

  @Mutation
  addMany(companies: ICompany[]) {
    companies.forEach((c) => this._data.set(c));
  }

  @Action
  reset() {
    super.reset();

    if (!UserModule.abilities.can(ActionEnum.READ, ResourceEnum.COMPANY, PartnerModule.partner.id)) {
      return;
    }

    this.fetchPartner(PartnerModule.partner.id);
  }
}

export const CompanyModule = getModule(CompanyPaginationStore);
